import { useCallback } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from '../utils/gsap'
import {
  BOARD_STATS_MAP_OPACITY_MIN,
  BOARD_STATS_MAP_SCALE_MIN,
  BOARD_STATS_PIN_TOP_EXTRA,
  BOARD_STATS_PIN_VH,
  BOARD_STATS_SCRUB,
  getBoardPinStart,
  getPinScrollDistance,
  lerp,
  smoothstep,
} from '../utils/scrollAnimation'
import { useGsapScroll } from './useGsapScroll'

function getMaxPan(layers) {
  const panEl = layers.pan
  const stageEl = layers.stage
  if (!panEl || !stageEl) return 0
  return Math.max(0, panEl.offsetHeight - stageEl.offsetHeight)
}

export function useStatsScrollReveal(sectionRef, pinRef, layerRefs, enabled) {
  const setup = useCallback(() => {
    const section = sectionRef.current
    const pin = pinRef.current
    const layers = layerRefs.current
    if (!section || !pin) return

    const mapEl = layers.map
    const leftEl = layers.left
    const rightEl = layers.right
    const panEl = layers.pan

    let maxPan = getMaxPan(layers)
    const setPanY = panEl ? gsap.quickSetter(panEl, 'y', 'px') : null
    const setMapOpacity = mapEl ? gsap.quickSetter(mapEl, 'opacity') : null
    const setLeftOpacity = leftEl ? gsap.quickSetter(leftEl, 'opacity') : null
    const setRightOpacity = rightEl ? gsap.quickSetter(rightEl, 'opacity') : null

    const applyFrame = (progress) => {
      const p = Math.max(0, Math.min(1, progress))

      // Phase 1 — map zoom + opacity
      const mapP = smoothstep(0, 0.32, p)
      if (setMapOpacity) {
        setMapOpacity(lerp(BOARD_STATS_MAP_OPACITY_MIN, 1, mapP))
      }
      if (mapEl) {
        const mapScale =
          BOARD_STATS_MAP_SCALE_MIN + (1 - BOARD_STATS_MAP_SCALE_MIN) * mapP
        mapEl.style.transform = `translate3d(0, 0, 0) scale(${mapScale})`
      }

      // Phase 2 — left stat (after map is fully revealed, before panning down)
      const leftP = smoothstep(0.32, 0.46, p)
      if (setLeftOpacity) setLeftOpacity(leftP)
      if (leftEl) {
        leftEl.style.transform = `translate3d(${-28 + 28 * leftP}px, 0, 0) scale(${0.94 + 0.06 * leftP})`
      }

      // Phase 3 — pan down to reveal bottom stat area
      const initialPan = Math.min(maxPan * 0.14, 80)
      const panP = smoothstep(0.46, 0.74, p)
      const panY = -initialPan - (maxPan - initialPan) * panP
      if (setPanY) setPanY(panY)

      // Phase 4 — right stat (once scroll has brought lower area into view)
      const rightP = smoothstep(0.62, 0.86, p)
      if (setRightOpacity) setRightOpacity(rightP)
      if (rightEl) {
        rightEl.style.transform = `translate3d(${28 - 28 * rightP}px, 0, 0) scale(${0.94 + 0.06 * rightP})`
      }
    }

    applyFrame(0)

    ScrollTrigger.create({
      trigger: section,
      start: getBoardPinStart(BOARD_STATS_PIN_TOP_EXTRA),
      end: () => `+=${getPinScrollDistance(BOARD_STATS_PIN_VH)}`,
      pin,
      pinSpacing: true,
      scrub: BOARD_STATS_SCRUB,
      invalidateOnRefresh: true,
      anticipatePin: 1,
      preventOverlaps: true,
      onRefresh: () => {
        maxPan = getMaxPan(layers)
      },
      onUpdate: (self) => applyFrame(self.progress),
    })
  }, [sectionRef, pinRef, layerRefs])

  useGsapScroll(sectionRef, enabled, setup)
}
